export interface ApiFailureContext {
  apiTraceId: string;
  method: string;
  route: string;
  errorCode: string;
  status: number | null;
  retryCount: number;
}

type TelemetryStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const STORAGE_KEY = "cariaca:api-failures:v1";
const MAX_BUFFERED_FAILURES = 20;

const UUID_PATH_SEGMENT = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i;
const NUMERIC_PATH_SEGMENT = /^\d+$/;

export const createApiTraceId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `webtrace-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const normalizeApiRoute = (url: unknown): string => {
  let path: string;
  try {
    path = new URL(String(url ?? "/unknown"), "https://cariaca.invalid").pathname;
  } catch {
    path = "/unknown";
  }
  return path
    .split("/")
    .map((segment) =>
      NUMERIC_PATH_SEGMENT.test(segment) ||
      UUID_PATH_SEGMENT.test(segment) ||
      /^[A-Za-z0-9_-]{20,}$/.test(segment)
        ? ":id"
        : segment.replace(/[^A-Za-z0-9_.:-]/g, "_")
    )
    .join("/");
};

/** Only transport failures and server failures are operational incidents. */
export const buildApiFailureContext = (error: any): ApiFailureContext | null => {
  const status = Number(error?.response?.status);
  const hasStatus = Number.isFinite(status);
  if (hasStatus && status < 500) return null;

  const config = error?.config;
  if (!config) return null;
  return {
    apiTraceId: String(config._traceId ?? "unknown"),
    method: String(config.method ?? "get").toUpperCase(),
    route: normalizeApiRoute(config.url),
    errorCode: String(error?.code ?? (hasStatus ? `HTTP_${status}` : "NETWORK_ERROR"))
      .replace(/[^A-Za-z0-9_.-]/g, "_")
      .slice(0, 50),
    status: hasStatus ? status : null,
    retryCount: Number(config._transientRetryCount ?? 0),
  };
};

const browserStorage = (): TelemetryStorage | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const readFailures = (storage: TelemetryStorage): ApiFailureContext[] => {
  try {
    const value = JSON.parse(storage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.slice(-MAX_BUFFERED_FAILURES) : [];
  } catch {
    storage.removeItem(STORAGE_KEY);
    return [];
  }
};

export const enqueueApiFailure = (
  failure: ApiFailureContext,
  storage: TelemetryStorage | null = browserStorage()
) => {
  if (!storage) return;
  try {
    const failures = [...readFailures(storage), failure].slice(-MAX_BUFFERED_FAILURES);
    storage.setItem(STORAGE_KEY, JSON.stringify(failures));
  } catch {
    // 저장소 차단/용량 초과가 원래 API 요청에 영향을 주면 안 된다.
  }
};

export const takeApiFailures = (
  storage: TelemetryStorage | null = browserStorage()
): ApiFailureContext[] => {
  if (!storage) return [];
  const failures = readFailures(storage);
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    return [];
  }
  return failures;
};
